import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setHttpAuthToken } from '@/core/api';
import { userHasPermission, userCanAccessPath } from '@/core/auth/permissions';
import {
  clearStoredSession,
  endSession,
  isAccessTokenExpired,
  onSessionEnd,
  readJwtExpSeconds,
  redirectToLogin,
} from '@/core/auth/sessionExpiry';
import {
  getAccessToken,
  getStoredUserJson,
  setStoredSession,
} from '@/core/auth/tokenStore';

const AuthContext = createContext(null);

function applyAuthToken(tokenData) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
  setHttpAuthToken(tokenData);
}

function clearAuthToken() {
  delete axios.defaults.headers.common['Authorization'];
  setHttpAuthToken(null);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const wipeLocalAuth = useCallback(() => {
    setToken(null);
    setUser(null);
    clearStoredSession();
    clearAuthToken();
  }, []);

  useEffect(() => {
    const storedToken = getAccessToken();
    const storedUser = getStoredUserJson();

    if (storedToken && storedUser) {
      if (isAccessTokenExpired(storedToken)) {
        clearStoredSession();
        clearAuthToken();
      } else {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          applyAuthToken(storedToken);
        } catch {
          clearStoredSession();
          clearAuthToken();
        }
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    return onSessionEnd(() => {
      wipeLocalAuth();
      redirectToLogin();
    });
  }, [wipeLocalAuth]);

  // Proactively end the session when the JWT exp is reached (oidc-lab: 1h).
  useEffect(() => {
    if (!token) return undefined;
    const exp = readJwtExpSeconds(token);
    if (exp == null) return undefined;
    const ms = exp * 1000 - Date.now() - 5_000;
    if (ms <= 0) {
      endSession('expired');
      return undefined;
    }
    const id = window.setTimeout(() => endSession('expired'), ms);
    return () => window.clearTimeout(id);
  }, [token]);

  const login = (tokenData, userData) => {
    setToken(tokenData);
    setUser(userData);
    setStoredSession(tokenData, userData);
    applyAuthToken(tokenData);
  };

  const setAccessToken = (newToken) => {
    setToken(newToken);
    setStoredSession(newToken, user);
    applyAuthToken(newToken);
  };

  const logout = () => {
    wipeLocalAuth();
    redirectToLogin();
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasPermission = (...codes) => {
    if (!user || codes.length === 0) return false;
    return codes.every((c) => userHasPermission(user, c));
  };

  const canAccessPath = (pathname) => userCanAccessPath(user, pathname);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, setAccessToken, logout, hasRole, hasPermission, canAccessPath }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
